import dynamic from 'next/dynamic'
import { useEffect, useRef } from 'react'
import { useButton } from 'react-aria'
import { Img } from '~/components/elements/Img'
import { ArrowLeftIcon } from '~/components/icons/generated/ArrowLeftIcon'
import { ArrowRightIcon } from '~/components/icons/generated/ArrowRightIcon'
import { useBinary } from '~/hooks/useBinary'
import { useCarousel } from '~/hooks/useCarousel'

const ThumbnailList = dynamic(
  () => import('src/components/modules/CarouselImages/ThumbnailList'),
  {
    ssr: false,
  }
)

type Props = {
  imageUrls: string[]
  alt: string
}

export const CarouselImages: React.FC<Props> = ({ imageUrls, alt }) => {
  const [isShowCurrentIndex, showCurrentIndex, hideCurrentIndex] =
    useBinary(false)

  const {
    currentIndex: currentImageIndex,
    next: onClickNext,
    prev: onClickPrev,
    goFirst: onClickFirst,
    goLast: onClickLast,
    go,
    ref: imagesContainerRef,
  } = useCarousel<HTMLDivElement>()
  const totalImageLength = imageUrls.length
  const hasSubImage = totalImageLength > 1
  const isFirstImage = currentImageIndex === 0
  const isLastImage = currentImageIndex === totalImageLength - 1

  useEffect(() => {
    showCurrentIndex()
    const timer = setTimeout(() => {
      hideCurrentIndex()
    }, 2500)

    return () => {
      clearTimeout(timer)
    }
  }, [currentImageIndex, showCurrentIndex, hideCurrentIndex])

  const prevButtonRef = useRef<HTMLButtonElement>(null)
  const { buttonProps: prevButtonProps } = useButton(
    {
      onPress: isFirstImage ? onClickLast : onClickPrev,
      'aria-label': '前のスライドに移動',
    },
    prevButtonRef
  )

  const nextButtonRef = useRef<HTMLButtonElement>(null)
  const { buttonProps: nextButtonProps } = useButton(
    {
      onPress: isLastImage ? onClickFirst : onClickNext,
      'aria-label': '次のスライドに移動',
    },
    nextButtonRef
  )

  return (
    <div>
      <div className="relative">
        <div
          ref={imagesContainerRef}
          className="relative flex aspect-[5/6] snap-x snap-mandatory overflow-x-auto hide-scroll"
        >
          {imageUrls.map((imageUrl, i) => {
            return (
              <div
                key={imageUrl}
                className="relative flex w-full shrink-0 snap-center"
              >
                <Img
                  className="object-contain"
                  src={imageUrl}
                  alt={`${alt}の${i + 1}枚目の写真`}
                  width={1000}
                  height={1200}
                  priority={i === 0}
                />
              </div>
            )
          })}
        </div>
        {hasSubImage && (
          <div className="absolute inset-0 text-[18px]">
            <button
              type="button"
              {...prevButtonProps}
              ref={prevButtonRef}
              className="absolute left-[10px] top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/15 pr-[2px] xl:left-6"
            >
              <ArrowLeftIcon className="mt-[-1px] h-[18px] text-white" />
            </button>
            <button
              type="button"
              {...nextButtonProps}
              ref={nextButtonRef}
              className="absolute right-[10px] top-1/2 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/15 pl-[2px] xl:right-6"
            >
              <ArrowRightIcon className="mt-[-1px] h-[18px] text-white" />
            </button>
          </div>
        )}
        {hasSubImage && (
          <div
            className={`absolute bottom-4 flex w-full justify-center transition ease-linear xl:hidden ${
              isShowCurrentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <p className="flex items-center justify-center rounded-[1em] bg-black/15 px-[11px] text-[11px] text-white">
              <span>{currentImageIndex + 1}</span>
              <span className="px-1">/</span>
              <span>{totalImageLength}</span>
            </p>
          </div>
        )}
      </div>
      <div className="hidden xl:block">
        <ThumbnailList
          imageUrls={imageUrls}
          alt={alt}
          currentIndex={currentImageIndex}
          onClickNth={go}
        />
      </div>
    </div>
  )
}
